import React, { useState, useEffect } from 'react';
import { Lock, Download, Users, RefreshCw } from 'lucide-react';
import axios from 'axios';

const AdminPanel = ({ apiUrl }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [bets, setBets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      const response = await axios.post(`${apiUrl}/admin/authenticate`, { password });
      if (response.data.success) {
        setIsAuthenticated(true);
        loadBets(password);
      }
    } catch (err) {
      setError('Invalid password');
    }
  };

  const loadBets = async (pwd) => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/admin/bets?password=${pwd}`);
      setBets(response.data);
    } catch (err) {
      setError('Failed to load bets');
    }
    setLoading(false);
  };

  const handleRefresh = () => {
    loadBets(password);
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${apiUrl}/admin/export-csv?password=${password}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'deepseek4_bets.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Failed to export CSV');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'linear-gradient(135deg, #0A0512 0%, #11081A 100%)' }}>
        <div className="w-full max-w-md">
          <div className="glass-card p-8 fade-in">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[#8E24F5] to-[#A64DF7] mb-4 purple-glow">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2">
                Admin Access
              </h2>
              <p className="text-[#A19EB5]">Enter password to continue</p>
            </div>

            <form onSubmit={handleLogin}>
              <input
                data-testid="admin-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="w-full mb-4"
                required
              />
              {error && (
                <p className="text-[#FF6B6B] text-sm mb-4">{error}</p>
              )}
              <button
                data-testid="admin-login-button"
                type="submit"
                className="purple-btn w-full"
              >
                Unlock Admin Panel
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6" style={{ background: 'linear-gradient(135deg, #0A0512 0%, #11081A 100%)' }}>
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8 fade-in">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-4xl sm:text-5xl font-black tracking-tighter mb-2">
                Admin Panel
              </h1>
              <p className="text-[#A19EB5] flex items-center gap-2">
                <Users className="w-4 h-4" />
                {bets.length} total submissions
              </p>
            </div>
            <div className="flex gap-3">
              <button
                data-testid="refresh-button"
                onClick={handleRefresh}
                className="purple-btn-outline flex items-center gap-2"
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                data-testid="export-csv-button"
                onClick={handleExportCSV}
                className="purple-btn flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8 fade-in" style={{ animationDelay: '0.1s' }}>
          <div className="dashboard-card p-6">
            <div className="text-xs uppercase tracking-[0.2em] text-[#A64DF7] mb-2 font-semibold">
              Total Bets
            </div>
            <div className="text-3xl font-bold">{bets.length}</div>
          </div>
          <div className="dashboard-card p-6">
            <div className="text-xs uppercase tracking-[0.2em] text-[#A64DF7] mb-2 font-semibold">
              Bet #1 Yes
            </div>
            <div className="text-3xl font-bold">
              {bets.filter(b => b.bet1_choice === 'yes').length}
            </div>
          </div>
          <div className="dashboard-card p-6">
            <div className="text-xs uppercase tracking-[0.2em] text-[#A64DF7] mb-2 font-semibold">
              Bet #2 Yes
            </div>
            <div className="text-3xl font-bold">
              {bets.filter(b => b.bet2_choice === 'yes').length}
            </div>
          </div>
          <div className="dashboard-card p-6">
            <div className="text-xs uppercase tracking-[0.2em] text-[#A64DF7] mb-2 font-semibold">
              Avg Wager
            </div>
            <div className="text-3xl font-bold">
              ${bets.length > 0 ? (bets.reduce((sum, b) => sum + b.total_wager, 0) / bets.length).toFixed(0) : 0}
            </div>
          </div>
        </div>

        {/* Bets Table */}
        <div className="glass-card p-6 fade-in overflow-x-auto" style={{ animationDelay: '0.2s' }}>
          {loading ? (
            <div className="text-center py-12 text-[#A19EB5]">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" />
              Loading bets...
            </div>
          ) : bets.length === 0 ? (
            <div className="text-center py-12 text-[#A19EB5]">
              No bets submitted yet
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Company</th>
                  <th>Bet #1</th>
                  <th>Wager</th>
                  <th>Bet #2</th>
                  <th>Wager</th>
                  <th>Date Bet</th>
                  <th>Wager</th>
                  <th>Total</th>
                  <th>Submitted</th>
                </tr>
              </thead>
              <tbody>
                {bets.map((bet, idx) => (
                  <tr key={idx}>
                    <td>{bet.email}</td>
                    <td>{bet.name || '-'}</td>
                    <td>{bet.company || '-'}</td>
                    <td className="uppercase">{bet.bet1_choice}</td>
                    <td className="font-bold">${bet.bet1_wager}</td>
                    <td className="uppercase">{bet.bet2_choice}</td>
                    <td className="font-bold">${bet.bet2_wager}</td>
                    <td className="text-sm">
                      {bet.bet3_date === 'after-30' ? 'After 30d' : new Date(bet.bet3_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </td>
                    <td className="font-bold">${bet.bet3_wager}</td>
                    <td className="font-bold text-[#A64DF7]">${bet.total_wager}</td>
                    <td className="text-sm text-[#A19EB5]">
                      {new Date(bet.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
